import React, { Fragment, useEffect, useState, useRef, useCallback } from "react";
import Input from "components/Input";
import styles from "./mfa.module.scss";
import { sendMfaOpt, verifyMfaOpt } from "repositories/user-repository";
import { withTranslation } from "react-i18next";
import Loader from "components/Loader";
import { generateDeviceId, getDeviceType } from "utils";
import { useLocation, useHistory } from "react-router-dom";
import { useMsal, useAccount } from "@azure/msal-react";
import { buildAcquireTokenSilentRequest } from "services/authProvider";
import {
  persistMfaValidatedForAccount,
  persistPendingMfaSendPayload,
  readPendingMfaSendPayload,
  clearPendingMfaSendPayload,
  resolveMfaHistoryId,
  MFA_INIT_SEND_TS_KEY,
} from "utils/mfaStorage";
import { useAppContext } from "context/AppContext";

/** Email MFA uses mfaType === 1; treat missing type as email (first-time SendEmailMfa). */
function isEmailMfaPayload(data) {
  return data?.mfaType === 1 || data?.mfaType == null;
}

/**
 * Normalize send payload so `id` is always a positive number when present.
 * @param {Record<string, unknown> | null | undefined} data
 */
function normalizeMfaSendData(data) {
  const id = resolveMfaHistoryId(data);
  if (id == null) return null;
  return { ...data, id };
}

function MultifactorAuth({ t, logoutFromMfa }) {
  const { setIsMfaValidate } = useAppContext();
  const history = useHistory();
  const location = useLocation();
  const { instance } = useMsal();
  const msalAccount = useAccount();
  const otpResponse = location?.state || {};
  const [seconds, setSeconds] = useState({ emailSeconds: 30, smsSeconds: 30 });
  const [otp, setOpt] = useState("");
  const [errorMsg, setError] = useState(null);
  const [otpData, setOtpData] = useState(null);
  const [loader, setLoader] = useState(false);

  /**
   * Returns the current id token — from localStorage if already acquired,
   * otherwise acquires it silently from MSAL (handles the race between
   * component mount and acquireTokenSilent in useAppSharedState).
   */
  const getIdToken = useCallback(async () => {
    const stored = localStorage.getItem("msal.idtoken");
    if (stored) return stored;
    if (!msalAccount) {
      return null;
    }
    try {
      const result = await instance.acquireTokenSilent(
        buildAcquireTokenSilentRequest(msalAccount),
      );
      if (result?.idToken) {
        localStorage.setItem("msal.idtoken", result.idToken);
        return result.idToken;
      }
    } catch (e) {
      console.warn("MFA: acquireTokenSilent fallback failed:", e);
    }
    return null;
  }, [instance, msalAccount]);
  const [verificationCount, setVerificationCount] = useState(null);
  const [isAlternateMethod, setAlternateMethod] = useState(false);
  const [rememberme, setRememberme] = useState(false);
  const [mobileNo, setMobileNo] = useState("");

  useEffect(() => {
    const countdown = setInterval(() => {
      setSeconds((prev) => {
        if (!otpData?.isPhoneActive) {
          return {
            ...prev,
            emailSeconds: prev.emailSeconds > 0 ? prev.emailSeconds - 1 : 0,
          };
        } else {
          return {
            ...prev,
            smsSeconds: prev.smsSeconds > 0 ? prev.smsSeconds - 1 : 0,
          };
        }
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [!otpData?.isPhoneActive]);

  /**
   * Apply a successful Send*Mfa `data` object into otpData / counters and
   * persist UserMfaHistoryId for verify (survives Redirect without router state).
   * @param {Record<string, unknown>} data
   * @returns {boolean}
   */
  const applySuccessfulSendData = useCallback((data) => {
    const normalized = normalizeMfaSendData(data);
    if (!normalized) {
      return false;
    }

    persistPendingMfaSendPayload(normalized);

    if (isEmailMfaPayload(normalized)) {
      setSeconds((prev) => ({ ...prev, emailSeconds: 30 }));
      setOtpData((prev) => ({
        ...prev,
        emailData: normalized,
        isPhoneActive: false,
      }));
      setVerificationCount((prev) => ({
        ...prev,
        emailData: {
          ...prev?.emailData,
          resendAttempts: normalized.resendAttempts,
          wrongAttempts: normalized.wrongAttempts,
        },
      }));
    } else {
      setSeconds((prev) => ({ ...prev, smsSeconds: 30 }));
      setOtpData((prev) => ({
        ...prev,
        phoneData: normalized,
        isPhoneActive: true,
      }));
      setVerificationCount((prev) => ({
        ...prev,
        phoneData: {
          ...prev?.phoneData,
          resendAttempts: normalized.resendAttempts,
          wrongAttempts: normalized.wrongAttempts,
        },
      }));
    }
    return true;
  }, []);

  useEffect(() => {
    const hasServerPayload =
      otpResponse &&
      Object.keys(otpResponse).length > 0 &&
      (typeof otpResponse.statusCode === "number" ||
        otpResponse.data != null);
    if (!hasServerPayload) {
      return;
    }

    if (isEmailMfaPayload(otpResponse?.data)) {
      setSeconds((prev) => ({ ...prev, emailSeconds: 30 }));
    }
    const statusIndicatesError =
      typeof otpResponse.statusCode === "number" &&
      otpResponse.statusCode !== 200;
    if (statusIndicatesError) {
      if (isEmailMfaPayload(otpResponse?.data)) {
        setError((prev) => ({ ...prev, otpEmailError: otpResponse?.message }));
        setVerificationCount((prev) => ({
          ...prev,
          emailData: {
            ...prev?.emailData,
            resendAttempts: otpResponse?.data?.resendAttempts + 1,
            wrongAttempts: otpResponse?.data?.wrongAttempts + 1,
          },
        }));
      }
    } else if (
      otpResponse?.data &&
      typeof otpResponse.data === "object" &&
      resolveMfaHistoryId(otpResponse.data) != null
    ) {
      setError((prev) => ({ ...prev, phoneError: "" }));
      setAlternateMethod(false);
      applySuccessfulSendData(otpResponse.data);
    }
  }, [otpResponse, applySuccessfulSendData]);

  const logout = async () => {
    try {
      clearPendingMfaSendPayload();
      logoutFromMfa();
    } catch (error) {
      console.error("Error during logout process:", error);
    }
  };

  const goBack = () => {
    if (!isAlternateMethod && !otpData?.isPhoneActive) {
      logout();
    } else {
      if (isAlternateMethod) {
        setAlternateMethod(false);
      }
      if (otpData?.isPhoneActive) {
        setAlternateMethod(true);
        setOtpData((prev) => ({ ...prev, isPhoneActive: false }));
        setError((prev) => ({ ...prev, otpSmsError: "" }));
        setMobileNo("");
        setOpt("");
      }
    }
  };

  async function sendOtp() {
    const deviceType = getDeviceType();
    try {
      setLoader(true);

      const token = await getIdToken();
      if (!token) {
        console.warn("MFA: no id token available, skipping sendOtp");
        setLoader(false);
        return;
      }

      const deviceId = generateDeviceId();
      const deviceTypeString =
        sessionStorage.getItem("deviceType") || deviceType;
      sessionStorage.setItem("deviceType", deviceTypeString);
      const forSms = isAlternateMethod || otpData?.isPhoneActive;
      const params = {
        deviceId,
        deviceType: deviceTypeString,
        ...(forSms && { contactNumber: mobileNo }),
      };
      const res = await sendMfaOpt(params, token, forSms);

      if (res?.statusCode === 200 && typeof res?.data === "boolean") {
        persistMfaValidatedForAccount(msalAccount);
        setIsMfaValidate(true);
        history.replace("/");
        return;
      }

      if (isEmailMfaPayload(res?.data)) {
        setSeconds((prev) => ({ ...prev, emailSeconds: 30 }));
      } else {
        setSeconds((prev) => ({ ...prev, smsSeconds: 30 }));
      }
      if (res?.statusCode !== 200) {
        if (isAlternateMethod) {
          setError((prev) => ({ ...prev, phoneError: res?.message }));
        } else {
          if (isEmailMfaPayload(res?.data)) {
            setError((prev) => ({ ...prev, otpEmailError: res?.message }));
            setVerificationCount((prev) => ({
              ...prev,
              emailData: {
                ...prev?.emailData,
                resendAttempts: res?.data?.resendAttempts + 1,
                wrongAttempts: res?.data?.wrongAttempts + 1,
              },
            }));
          } else {
            setError((prev) => ({ ...prev, otpSmsError: res?.message }));
            setVerificationCount((prev) => ({
              ...prev,
              phoneData: {
                ...prev?.phoneData,
                resendAttempts: res?.data?.resendAttempts + 1,
                wrongAttempts: res?.data?.wrongAttempts + 1,
              },
            }));
          }
        }
      } else {
        setError((prev) => ({ ...prev, phoneError: "" }));
        setAlternateMethod(false);
        if (
          res?.data &&
          typeof res.data === "object" &&
          resolveMfaHistoryId(res.data) != null
        ) {
          applySuccessfulSendData(res.data);
        } else if (res?.data?.mfaType === 1) {
          // Preserve prior behavior if API omits id unexpectedly.
          setOtpData((prev) => ({
            ...prev,
            emailData: res?.data,
            isPhoneActive: false,
          }));
          setVerificationCount((prev) => ({
            ...prev,
            emailData: {
              ...prev?.emailData,
              resendAttempts: res?.data?.resendAttempts,
              wrongAttempts: res?.data?.wrongAttempts,
            },
          }));
        } else {
          setOtpData((prev) => ({
            ...prev,
            phoneData: res?.data,
            isPhoneActive: true,
          }));
          setVerificationCount((prev) => ({
            ...prev,
            phoneData: {
              ...prev?.phoneData,
              resendAttempts: res?.data?.resendAttempts,
              wrongAttempts: res?.data?.wrongAttempts,
            },
          }));
        }
      }
    } catch (err) {
      setLoader(false);
    } finally {
      setLoader(false);
    }
  }

  const initialSendGuardRef = useRef(false);

  /**
   * Redirect from App (no router state) skips useMfa preflight; ensure SendEmailMfa still runs once.
   * If useMfa already sent OTP and stored pending history, hydrate from sessionStorage instead of
   * skipping with an empty otpData (which caused VerifyMfaOtp without UserMfaHistoryId).
   */
  useEffect(() => {
    if (!msalAccount) {
      return;
    }

    const state = location.state;
    const hasValidRouterState =
      state != null &&
      typeof state === "object" &&
      Object.keys(state).length > 0 &&
      (typeof state.statusCode === "number" || state.data != null);

    if (hasValidRouterState) {
      return;
    }

    const pending = readPendingMfaSendPayload();
    if (pending && resolveMfaHistoryId(pending) != null) {
      applySuccessfulSendData(pending);
      return;
    }

    const now = Date.now();
    const prev = sessionStorage.getItem(MFA_INIT_SEND_TS_KEY);
    const recentlySent = prev && now - Number(prev) < 4000;

    // Recent send without recoverable history id — wait briefly then re-read pending;
    // if still missing, send again so Verify never runs with an empty id.
    if (recentlySent) {
      const retryTimer = window.setTimeout(() => {
        const delayedPending = readPendingMfaSendPayload();
        if (delayedPending && resolveMfaHistoryId(delayedPending) != null) {
          applySuccessfulSendData(delayedPending);
          return;
        }
        if (initialSendGuardRef.current) {
          return;
        }
        initialSendGuardRef.current = true;
        sessionStorage.setItem(MFA_INIT_SEND_TS_KEY, String(Date.now()));
        void sendOtp();
      }, 500);
      return () => window.clearTimeout(retryTimer);
    }

    if (initialSendGuardRef.current) {
      return;
    }
    initialSendGuardRef.current = true;
    sessionStorage.setItem(MFA_INIT_SEND_TS_KEY, String(now));

    void sendOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot; msalAccount dep ensures account is ready before calling sendOtp
  }, [msalAccount, applySuccessfulSendData]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${
      remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds
    }`;
  };

  const handleInputChange = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      setOpt(e.target.value);
    }
  };

  const handleMobileNo = (e) => {
    if (/^\d*$/.test(e.target.value)) {
      setMobileNo(e.target.value);
    }
  };

  const veryifyOtp = async () => {
    let errorMsg = "";
    if (!otp.trim().length) {
      errorMsg = t("form.errors.emptyField", {
        field: t("otp"),
      });
    } else if (otp?.length !== 6) {
      errorMsg = t("mfaAuthenticate.otpLength");
    }

    const isPhone = Boolean(otpData?.isPhoneActive);
    const channelData = isPhone ? otpData?.phoneData : otpData?.emailData;
    let historyId = resolveMfaHistoryId(channelData);
    if (historyId == null) {
      const pending = readPendingMfaSendPayload();
      if (pending && (isPhone ? !isEmailMfaPayload(pending) : isEmailMfaPayload(pending))) {
        historyId = resolveMfaHistoryId(pending);
      } else if (!isPhone) {
        // Email is default channel; accept pending email payload even if mfaType missing.
        historyId = resolveMfaHistoryId(pending);
      }
    }

    if (!errorMsg && historyId == null) {
      setError((prev) => ({
        ...prev,
        ...(isPhone
          ? { otpSmsError: t("mfaAuthenticate.sessionExpiredResend") }
          : { otpEmailError: t("mfaAuthenticate.sessionExpiredResend") }),
      }));
      return;
    }

    if (errorMsg) {
      setError((prev) => ({ ...prev, otpError: errorMsg }));
    } else {
      setError("");
    }
    if (!errorMsg) {
      try {
        setLoader(true);
        const token = await getIdToken();
        if (!token) {
          setLoader(false);
          return;
        }
        const params = {
          UserMfaHistoryId: historyId,
          rememberme,
          Code: otp,
        };

        const res = await verifyMfaOpt(params, token);
        if (!res?.data?.isMfaVerified) {
          if (res?.data?.mfaType === 1) {
            setError((prev) => ({ ...prev, otpEmailError: res?.message }));
            setVerificationCount((prev) => ({
              ...prev,
              emailData: {
                ...prev?.emailData,
                wrongAttempts: prev?.emailData?.wrongAttempts + 1,
              },
            }));
          } else {
            setError((prev) => ({ ...prev, otpSmsError: res?.message }));
            setVerificationCount((prev) => ({
              ...prev,
              phoneData: {
                ...prev?.phoneData,
                wrongAttempts: prev?.emailData?.wrongAttempts + 1,
              },
            }));
          }
        }
        if (res?.data?.isMfaVerified) {
          persistMfaValidatedForAccount(msalAccount);
          setIsMfaValidate(true);
          history.replace("/");
        }
      } catch (err) {
        setLoader(false);
      } finally {
        setLoader(false);
      }
    }
  };

  const handleResend = () => {
    sendOtp();
    if (!otpData?.isPhoneActive) {
      setVerificationCount((prev) => ({
        ...prev,
        emailData: {
          ...prev?.emailData,
          resendAttempts: prev?.phoneData?.resendAttempts + 1,
        },
      }));
    } else {
      setVerificationCount((prev) => ({
        ...prev,
        phoneData: {
          ...prev?.phoneData,
          resendAttempts: prev?.phoneData?.resendAttempts + 1,
        },
      }));
    }
  };

  const isAtemptsFailedForEmail =
    verificationCount?.emailData?.resendAttempts > 3 ||
    verificationCount?.emailData?.wrongAttempts > 3;
  const isAtemptsFailedForPhone =
    verificationCount?.phoneData?.resendAttempts > 3 ||
    verificationCount?.phoneData?.wrongAttempts > 3;
  const totalAttemsLeft = !!otpData
    ? 3 -
      (!otpData?.isPhoneActive
        ? (otpData?.emailData?.resendAttempts ?? 0)
        : (otpData?.phoneData?.resendAttempts ?? 0))
    : 3;

  const activeChannelHistoryId = resolveMfaHistoryId(
    otpData?.isPhoneActive ? otpData?.phoneData : otpData?.emailData,
  );
  const pendingHistoryId = resolveMfaHistoryId(readPendingMfaSendPayload());
  const hasVerifiableHistoryId =
    activeChannelHistoryId != null || pendingHistoryId != null;

  return (
    <div className="p-2">
      {loader && <Loader />}
      <div className={styles.mfa_container}>
        <div className={styles.mfa_left_side}>
          <div className={styles.logo}>
            <img
              alt="logo"
              src="https://mxhhstagingstorageacc.blob.core.windows.net/auth/miraxis-logo.svg"
            />
          </div>
          <div className={styles.signup_block_link}>
            <img
              src="https://mxhhstagingstorageacc.blob.core.windows.net/auth/login-img.png"
              alt=""
            />
          </div>
        </div>
        <div className={styles.mfa_right_side}>
          <div className={styles.field_group}>
            <button className="back-btn" onClick={goBack}>
              <div className="back-btn-text">
                <span className="ico">
                  <img
                    src={require("assets/images/arrow-back-icon.svg").default}
                    alt="arrow"
                  />
                </span>
                {!isAlternateMethod && !otpData?.isPhoneActive
                  ? t("mfaAuthenticate.backToLogin")
                  : t("mfaAuthenticate.back")}
              </div>
            </button>
            {!isAlternateMethod ? (
              <Fragment>
                <h2 className={styles.title}>
                  {!otpData?.isPhoneActive
                    ? t("mfaAuthenticate.emiltTitle")
                    : t("mfaAuthenticate.phoneTitle")}
                </h2>
                <p className={styles.sub_title}>
                  {!otpData?.isPhoneActive
                    ? t("mfaAuthenticate.emailSubTitle", {
                        email:
                          msalAccount?.idTokenClaims?.emails?.[0] ??
                          msalAccount?.idTokenClaims?.email ??
                          msalAccount?.username,
                      })
                    : t("mfaAuthenticate.phoneSubTitle", {
                        mobileNo: mobileNo,
                      })}
                </p>
                <Input
                  Type="text"
                  Placeholder={t("mfaAuthenticate.enterOtp")}
                  HandleChange={handleInputChange}
                  Name="otp"
                  Error={
                    !otpData?.isPhoneActive
                      ? errorMsg?.otpEmailError
                      : errorMsg?.otpSmsError
                  }
                  Value={otp}
                  MaxLength={6}
                  Disabled={
                    !otpData?.isPhoneActive
                      ? isAtemptsFailedForEmail
                      : isAtemptsFailedForPhone
                  }
                />
                <div className="ch-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={rememberme}
                      onChange={() => setRememberme(!rememberme)}
                    />
                    <span> {t("mfaAuthenticate.rememberMe")}</span>
                  </label>
                </div>
                <div className={styles.buttons}>
                  <button
                    id="next"
                    tabIndex={0}
                    onClick={veryifyOtp}
                    disabled={
                      !hasVerifiableHistoryId ||
                      (!otpData?.isPhoneActive
                        ? isAtemptsFailedForEmail
                        : isAtemptsFailedForPhone)
                    }
                  >
                    {t("mfaAuthenticate.submit")}
                  </button>
                </div>
                <div className="mt-5">
                  <div className={styles.verification_container}>
                    <div className={styles.btn_time}>
                      <button
                        className={styles.resend_btn + " " + "link-btn"}
                        disabled={
                          (!otpData?.isPhoneActive
                            ? seconds?.emailSeconds > 0
                            : seconds?.smsSeconds > 0) || totalAttemsLeft === 0
                        }
                        onClick={handleResend}
                      >
                        {t("mfaAuthenticate.resend")}
                      </button>
                      <p className={styles.verification_text}>
                        {t("mfaAuthenticate.verifcationCode")}{" "}
                        <span>
                          {formatTime(
                            !otpData?.isPhoneActive
                              ? seconds?.emailSeconds
                              : seconds?.smsSeconds
                          )}
                        </span>
                      </p>
                    </div>
                    <p className={styles.verification_text}>
                      {totalAttemsLeft} {t("mfaAuthenticate.attemptLeft")}
                    </p>
                  </div>
                  <hr />
                  {!otpData?.isPhoneActive && (
                    <div className={styles.verification_container}>
                      <p className={styles.verification_text}>
                        {t("mfaAuthenticate.unableToAccessEmail")}
                      </p>
                      <div className={styles.btn_time}>
                        <button
                          className={styles.resend_btn + " " + "link-btn"}
                          onClick={() => {
                            setAlternateMethod(true);
                            setOpt("");
                          }}
                        >
                          {t("mfaAuthenticate.tryAlternateMethod")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Fragment>
            ) : (
              <Fragment>
                <h2 className={styles.title}>
                  {t("mfaAuthenticate.enterMobileNo")}
                </h2>
                <p className={styles.sub_title}>
                  {t("mfaAuthenticate.mobileMsg")}
                </p>
                <Input
                  Type="text"
                  Placeholder={t("mfaAuthenticate.enterNumber")}
                  Name="mobileNo"
                  HandleChange={handleMobileNo}
                  Value={mobileNo}
                  MaxLength={10}
                  Error={errorMsg?.phoneError}
                />
                <div className={styles.buttons}>
                  <button id="next" tabIndex={0} onClick={sendOtp}>
                    {t("mfaAuthenticate.sendCode")}
                  </button>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(MultifactorAuth);
